// Too Cherry product page: bundle cards (each pair gets its own colour and size), photo gallery,
// size guide with foot-length finder, and add to cart through the theme's cart drawer.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

// Size chart columns. The product's size values match one of them (usually EU, or a market's
// own label such as "US 7.5"), and shoppers see their region's column first.
const SIZE_COLUMNS = ['EU', 'UK', 'US', 'AU'];

class TcProduct extends HTMLElement {
  #abort = new AbortController();
  #data = { variants: [], colours: [], sizes: [], current: '', media: {}, chart: '', title: '' };
  #chart = [];
  #rows = [];
  #qty = 1;
  #galleryColour = '';

  connectedCallback() {
    const raw = this.querySelector('[data-tc-data]')?.textContent;
    if (raw) this.#data = JSON.parse(raw);
    this.#chart = String(this.#data.chart || '')
      .split(/\r?\n/)
      .map((line) => line.split('|').map((s) => s.trim()))
      .filter((cells) => cells.length >= 5 && !Number.isNaN(parseFloat(cells[4])));
    this.#rows = [0, 1, 2].map(() => ({ colour: this.#data.current, size: '' }));
    this.#galleryColour = this.#data.current;

    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.querySelector('form')?.addEventListener('submit', (e) => { e.preventDefault(); this.#add(); }, { signal });
    this.querySelector('[data-tc-foot]')?.addEventListener('input', this.#onFoot, { signal });
    this.querySelector('[data-tc-track]')?.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    document.addEventListener('click', this.#onColourTile, { signal });

    const add = this.querySelector('[data-tc-add]');
    const sticky = this.querySelector('[data-tc-sticky]');
    if (add && sticky) {
      this.observer = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-on', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(add);
    }

    this.#renderChart();
    const checked = this.querySelector('.tc-bundle input:checked');
    this.#setQty(Number(checked?.value || 1));
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.observer?.disconnect();
  }

  get #col() {
    return Math.max(0, SIZE_COLUMNS.indexOf(this.dataset.sizeRegion));
  }

  #chartRow(size) {
    return this.#chart.find((c) => c.slice(0, 4).includes(size));
  }

  #sizeFor(row) {
    return this.#data.sizes.find((s) => row.slice(0, 4).includes(s)) || row[0];
  }

  // "US 7.5 · EU 38" for a US shopper, "EU 38" in EU-size countries.
  #label(size) {
    const row = this.#chartRow(size);
    if (!row) return size;
    const col = this.#col;
    return col === 0 ? row[0] : `${row[col]} · ${row[0]}`;
  }

  #variant(colour, size) {
    const ci = Number(this.dataset.colourIndex);
    const si = Number(this.dataset.sizeIndex);
    return this.#data.variants.find((v) => v.options[ci] === colour && v.options[si] === size);
  }

  #setQty(qty) {
    this.#qty = qty;
    for (const card of this.querySelectorAll('.tc-bundle')) {
      const selected = Number(card.dataset.qty) === qty;
      card.classList.toggle('is-selected', selected);
      const radio = card.querySelector('input[type="radio"]');
      if (radio) radio.checked = selected;
      const rows = card.querySelector('[data-tc-rows]');
      if (rows) rows.innerHTML = selected ? this.#rowsHtml(qty) : '';
    }
    const card = this.querySelector('.tc-bundle.is-selected');
    if (card) {
      // Prices arrive already formatted in the shopper's currency (see the bundle cards' Liquid).
      const { total, compare, each, title } = card.dataset;
      this.#text('[data-tc-total]', total);
      this.#text('[data-tc-compare]', compare);
      this.#text('[data-tc-each]', each ? `${each} each` : '');
      this.#text('[data-tc-atc-price]', `· ${total}`);
      this.#text('[data-tc-atc-compare]', compare);
      this.#text('[data-tc-sticky-info]', `${title} · ${total}`);
    }
    this.#setMessage('');
  }

  #rowsHtml(qty) {
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    let html = '';
    for (let i = 0; i < qty; i++) {
      const row = this.#rows[i];
      const hex = this.#data.colours.find((c) => c.name === row.colour)?.hex || '#ccc';
      const colourOpts = this.#data.colours
        .map((c) => `<option value="${esc(c.name)}"${c.name === row.colour ? ' selected' : ''}>${esc(c.name)}</option>`)
        .join('');
      const sizeOpts = this.#data.sizes
        .map((s) => {
          const v = this.#variant(row.colour, s);
          const off = !v || !v.available;
          return `<option value="${esc(s)}"${s === row.size ? ' selected' : ''}${off ? ' disabled' : ''}>${esc(this.#label(s))}${off ? ' (sold out)' : ''}</option>`;
        })
        .join('');
      const id = `${this.dataset.sectionId}-${i}`;
      if (qty === 1) {
        // One pair: the colour comes from the swatches, so only the size is asked for here.
        html += `<div class="tc-row tc-row--single" data-row="0">
          <span class="tc-select">
            <label class="tc-sr" for="tc-size-${id}">Size</label>
            <select id="tc-size-${id}" data-tc-size-select><option value="">Choose your size</option>${sizeOpts}</select>
          </span>
        </div>`;
        continue;
      }
      html += `<div class="tc-row" data-row="${i}">
        <span class="tc-row__n">${qty > 1 ? i + 1 : ''}</span>
        <span class="tc-select tc-select--colour">
          <span class="tc-select__dot" style="--c:${esc(hex)}"></span>
          <label class="tc-sr" for="tc-colour-${id}">Colour${qty > 1 ? ` for pair ${i + 1}` : ''}</label>
          <select id="tc-colour-${id}" data-tc-colour-select>${colourOpts}</select>
        </span>
        <span class="tc-select">
          <label class="tc-sr" for="tc-size-${id}">Size${qty > 1 ? ` for pair ${i + 1}` : ''}</label>
          <select id="tc-size-${id}" data-tc-size-select><option value="">Size</option>${sizeOpts}</select>
        </span>
      </div>`;
    }
    return html;
  }

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('.tc-bundle input[type="radio"]')) {
      this.#setQty(Number(target.value));
      return;
    }
    const rowEl = target.closest('[data-row]');
    if (!rowEl || !(target instanceof HTMLSelectElement)) return;
    const i = Number(rowEl.dataset.row);
    if (target.matches('[data-tc-colour-select]')) {
      this.#rows[i].colour = target.value;
      const v = this.#variant(target.value, this.#rows[i].size);
      if (!v || !v.available) this.#rows[i].size = '';
      const rows = this.querySelector('.tc-bundle.is-selected [data-tc-rows]');
      if (rows) rows.innerHTML = this.#rowsHtml(this.#qty);
      this.#showColour(target.value);
    }
    if (target.matches('[data-tc-size-select]')) {
      this.#rows[i].size = target.value;
      target.classList.remove('is-missing');
      this.#setMessage('');
    }
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const card = target.closest('.tc-bundle');
    if (card && !target.closest('select, label, input') && !card.classList.contains('is-selected')) {
      this.#setQty(Number(card.dataset.qty));
    }
    if (target.closest('[data-tc-prev]')) this.#step(-1);
    if (target.closest('[data-tc-next]')) this.#step(1);
    const swatch = target.closest('[data-tc-swatch]');
    if (swatch) this.#pickColour(swatch.dataset.tcSwatch, false);
    const thumb = target.closest('[data-tc-thumb]');
    if (thumb) this.#goTo(Number(thumb.dataset.tcThumb));
    if (target.closest('[data-tc-guide-open]')) this.querySelector('[data-tc-guide]')?.showModal();
    if (target.closest('[data-tc-guide-close]')) this.querySelector('[data-tc-guide]')?.close();
    const use = target.closest('[data-tc-use-size]');
    if (use) this.#useSize(use.dataset.tcUseSize);
    if (target.closest('[data-tc-sticky-add]')) this.#add();
  };

  // A swatch (or a colour tile elsewhere on the page) puts that colour on every pair; shoppers can
  // still change one pair's colour in its row to mix.
  #pickColour(colour, scroll) {
    if (!this.#data.colours.some((c) => c.name === colour)) return;
    for (const row of this.#rows) {
      row.colour = colour;
      const v = this.#variant(colour, row.size);
      if (!v || !v.available) row.size = '';
    }
    for (const s of this.querySelectorAll('[data-tc-swatch]')) s.setAttribute('aria-pressed', String(s.dataset.tcSwatch === colour));
    const swatchImg = this.querySelector(`[data-tc-swatch="${CSS.escape(colour)}"] img`);
    if (swatchImg) for (const pic of this.querySelectorAll('[data-tc-bundle-pic]')) pic.src = swatchImg.src;
    this.#text('[data-tc-colour-name]', colour);
    const rows = this.querySelector('.tc-bundle.is-selected [data-tc-rows]');
    if (rows) rows.innerHTML = this.#rowsHtml(this.#qty);
    this.#showColour(colour);
    if (scroll) this.querySelector('.tc-buy')?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
  }

  #onColourTile = (event) => {
    const tile = event.target instanceof Element ? event.target.closest('[data-tc-colour]') : null;
    if (!tile || !this.#data.colours.some((c) => c.name === tile.dataset.tcColour)) return;
    event.preventDefault();
    this.#pickColour(tile.dataset.tcColour, true);
  };

  /* gallery */
  #showColour(colour) {
    if (colour === this.#galleryColour) return;
    const media = this.#data.media[colour];
    if (!media || !media.length) return;
    this.#galleryColour = colour;
    const track = this.querySelector('[data-tc-track]');
    const thumbs = this.querySelector('[data-tc-thumbs]');
    if (track) {
      track.innerHTML = media
        .map((m) => `<div class="tc-gal__slide"><img src="${m.src}" alt="${this.#data.title} in ${colour}" width="1400" height="1400"></div>`)
        .join('');
      track.scrollTo({ left: 0 });
    }
    if (thumbs) {
      thumbs.innerHTML = media
        .map((m, i) => `<button type="button" class="tc-gal__thumb" data-tc-thumb="${i}" aria-label="Show photo ${i + 1}"${i === 0 ? ' aria-current="true"' : ''}><img src="${m.thumb}" alt="" width="200" height="200" loading="lazy"></button>`)
        .join('');
    }
  }

  // Slides can be narrower than the track (phones show the next photo peeking in), so step by a
  // slide's width plus the gap between slides.
  #stepWidth() {
    const track = this.querySelector('[data-tc-track]');
    const [a, b] = track ? track.children : [];
    if (!a) return 1;
    return b ? b.offsetLeft - a.offsetLeft : a.offsetWidth;
  }

  #index() {
    const track = this.querySelector('[data-tc-track]');
    return track ? Math.round(track.scrollLeft / this.#stepWidth()) : 0;
  }

  #goTo(i) {
    const track = this.querySelector('[data-tc-track]');
    if (!track) return;
    const count = track.children.length;
    const next = (i + count) % count;
    track.scrollTo({ left: next * this.#stepWidth(), behavior: reduceMotion() ? 'auto' : 'smooth' });
    const thumb = this.querySelector(`[data-tc-thumb="${next}"]`);
    thumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  #step(dir) {
    this.#goTo(this.#index() + dir);
  }

  #onScroll = () => {
    const i = this.#index();
    for (const t of this.querySelectorAll('[data-tc-thumb]')) {
      t.setAttribute('aria-current', String(Number(t.dataset.tcThumb) === i));
    }
  };

  /* size guide */
  #renderChart() {
    const table = this.querySelector('[data-tc-chart]');
    if (!table) return;
    const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const order = [this.#col, ...[0, 1, 2, 3].filter((i) => i !== this.#col)];
    const head = table.querySelector('thead tr');
    if (head) head.innerHTML = `${order.map((i) => `<th scope="col">${SIZE_COLUMNS[i]}</th>`).join('')}<th scope="col">Foot (cm)</th>`;
    const number = (cell) => esc(cell.replace(/^[A-Z]+\s*/, ''));
    table.querySelector('tbody').innerHTML = this.#chart
      .map((c) => `<tr data-size="${esc(this.#sizeFor(c))}">${order
        .map((i, n) => (n === 0 ? `<td><b>${number(c[i])}</b></td>` : `<td>${number(c[i])}</td>`))
        .join('')}<td>${parseFloat(c[4]).toFixed(1)}</td></tr>`)
      .join('');
  }

  #onFoot = (event) => {
    const out = this.querySelector('[data-tc-finder-out]');
    if (!out) return;
    for (const tr of this.querySelectorAll('[data-tc-chart] tr')) tr.classList.remove('is-match');
    const cm = parseFloat(String(event.target.value).replace(',', '.'));
    if (!cm || cm < 20 || cm > 32) {
      out.textContent = '';
      return;
    }
    const hit = this.#chart.find((c) => parseFloat(c[4]) >= cm);
    if (!hit) {
      const last = this.#chart[this.#chart.length - 1];
      out.textContent = last ? `That's longer than our ${this.#label(this.#sizeFor(last))}. Email us and we'll help.` : '';
      return;
    }
    const size = this.#sizeFor(hit);
    this.querySelector(`[data-tc-chart] tr[data-size="${CSS.escape(size)}"]`)?.classList.add('is-match');
    const between = parseFloat(hit[4]) !== cm ? " You're between sizes, so we've picked the bigger one." : '';
    out.textContent = `Your size: ${this.#label(size)}.${between} `;
    const use = document.createElement('button');
    use.type = 'button';
    use.className = 'tc-link';
    use.dataset.tcUseSize = size;
    use.textContent = 'Use this size';
    out.append(use);
  };

  #useSize(size) {
    const i = Math.max(0, this.#rows.slice(0, this.#qty).findIndex((r) => !r.size));
    const v = this.#variant(this.#rows[i].colour, size);
    if (v && v.available) this.#rows[i].size = size;
    const rows = this.querySelector('.tc-bundle.is-selected [data-tc-rows]');
    if (rows) rows.innerHTML = this.#rowsHtml(this.#qty);
    this.querySelector('[data-tc-guide]')?.close();
  }

  /* cart */
  #text(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }

  #setMessage(text) {
    this.#text('[data-tc-msg]', text);
  }

  async #add() {
    const rows = this.#rows.slice(0, this.#qty);
    const missing = rows.findIndex((r) => !r.size);
    if (missing >= 0) {
      const select = this.querySelector(`.tc-bundle.is-selected [data-row="${missing}"] [data-tc-size-select]`);
      select?.classList.add('is-missing');
      select?.focus();
      this.#setMessage(this.#qty > 1 ? `Choose a size for pair ${missing + 1}.` : 'Choose your size.');
      return;
    }

    const counts = new Map();
    for (const r of rows) {
      const v = this.#variant(r.colour, r.size);
      if (!v || !v.available) {
        this.#setMessage(`${r.colour}, ${this.#label(r.size)} is sold out.`);
        return;
      }
      counts.set(v.id, (counts.get(v.id) || 0) + 1);
    }

    const add = this.querySelector('[data-tc-add]');
    add?.setAttribute('disabled', '');
    this.#setMessage('');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    const body = new FormData();
    let n = 0;
    for (const [id, quantity] of counts) {
      body.set(`items[${n}][id]`, String(id));
      body.set(`items[${n}][quantity]`, String(quantity));
      n++;
    }
    if (sectionIds.length) body.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [...counts].map(([id, quantity]) => ({ merchandiseId: String(id), quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((r) => r.json());
      const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError,
          items: cart.items,
          source: 'tc-product',
          sourceId: this.dataset.sectionId,
          itemCount: this.#qty,
          productId: this.dataset.productId,
          sections: response.sections,
        },
      });
      if (didError) {
        this.#setMessage(response.description || response.message || 'Sorry, that could not be added.');
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
      } else {
        this.#setMessage('Added to your cart.');
      }
    } catch (error) {
      deferred.reject(error);
      this.#setMessage('Sorry, something went wrong. Please try again.');
    } finally {
      add?.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('tc-product')) customElements.define('tc-product', TcProduct);
